/**
 * Renders an image as HTML string (non-React version of renderImage)
 * Used in vanilla JavaScript contexts like query-loop/view.js
 * 
 * @param {Object} params - Image rendering parameters
 * @param {number} params.imageId - WordPress attachment ID
 * @param {string} params.imageUrl - Original image URL
 * @param {string} params.imageAlt - Image alt text
 * @param {Object} params.sizes - Object with breakpoint keys and [width, height] arrays
 * @param {Object} params.focalPoints - Object with breakpoint keys and {x, y} focal point objects
 * @param {boolean} params.editMode - Whether in edit mode (default: false)
 * @param {boolean} params.disableLazyLoading - Disable lazy loading (default: false)
 * @param {boolean} params.disable2x - Disable 2x retina images (default: false)
 * @param {boolean} params.scaleOnly - Use scale-only mode (default: false)
 * @returns {string} HTML string for picture element
 */
export default function renderImageHTML(params) {
  const defaults = {
    imageId: null,
    imageAlt: null,
    imageUrl: null,
    sizes: {},
    focalPoints: {},
    editMode: false,
    disableLazyLoading: false,
    disable2x: false,
    scaleOnly: false,
  };

  let {
    imageId,
    imageAlt,
    imageUrl,
    sizes,
    focalPoints,
    editMode,
    disableLazyLoading,
    disable2x,
    scaleOnly
  } = { ...defaults, ...params };

  if (!imageUrl) return '';

  let sources = [];

  let lastSlashIndex = imageUrl.lastIndexOf('/');
  const filename = imageUrl.substring(lastSlashIndex + 1);
  const extStart = filename.lastIndexOf('.');
  const filepathNoExt = `/wp-content/uploads/modulr-images/${imageId}/` + filename.substring(0, extStart);
  const ext = filename.substring(extStart + 1);
  const useWebp = window.modulrSettings?.useWebp ? !!parseInt(window.modulrSettings.useWebp) : true;

  const siteUrl = window.location.origin;

  let fallback = ext !== 'svg' && !editMode ? `${siteUrl}/wp-content/uploads/modulr-images/${imageId}/${filename}` : imageUrl;
  let preferred = fallback;
  if (ext !== 'webp' && ext !== 'svg' && !editMode && useWebp) preferred += '.webp';
  if (editMode && ext !== 'svg' && fallback !== imageUrl) fallback = siteUrl + fallback;

  if (typeof editMode === 'undefined') editMode = false;
  if (typeof focalPoints === 'undefined') focalPoints = {};

  // Get first focal point for object-position style (normalize so x/y are numbers)
  const rawFirst = Object.keys(focalPoints).length > 0 && focalPoints[Object.keys(focalPoints)[0]] ?
    focalPoints[Object.keys(focalPoints)[0]] :
    { x: 0.5, y: 0.5 };
  const firstFocalPoint = {
    x: typeof rawFirst.x === 'number' ? rawFirst.x : parseFloat(rawFirst.x) || 0.5,
    y: typeof rawFirst.y === 'number' ? rawFirst.y : parseFloat(rawFirst.y) || 0.5,
  };

  const imgStyle = `object-position: ${firstFocalPoint.x * 100}% ${firstFocalPoint.y * 100}%`;

  if (ext !== 'svg' && !editMode) {
    Object.keys(sizes).forEach(sizeKey => {
      let focalPoint = '', media = false, srcset, srcsetWebp;

      if (scaleOnly) {
        focalPoint = `-s`;
      } else if (sizeKey in focalPoints && typeof focalPoints[sizeKey] !== 'undefined') {
        focalPoint = `-f${Math.round(focalPoints[sizeKey].x * 100)}_${Math.round(focalPoints[sizeKey].y * 100)}`;
      } else {
        focalPoint = `-f50_50`;
      }

      const size1x = sizes[sizeKey].join('x');
      const size2x = `${sizes[sizeKey][0] * 2}x${sizes[sizeKey][1] * 2}`;

      const filepath1x = `${filepathNoExt}-${size1x}${focalPoint}.${ext}`;
      const filepath2x = `${filepathNoExt}-${size2x}${focalPoint}.${ext}`;

      srcset = `${filepath1x} 1x${disable2x ? '' : ', ' + filepath2x + ' 2x'}`;
      srcsetWebp = `${filepath1x}.webp 1x${disable2x ? '' : ', ' + filepath2x + '.webp 2x'}`;

      let type = ext;
      if (ext === 'jpg') type = 'jpeg';

      if (sizeKey !== 'default' && sizeKey !== 0 && sizeKey !== '0') {
        media = `(${sizeKey})`;
      }

      if (media === '(0)') media = false;

      if (useWebp) {
        sources.push(`<source${media ? ` media="${media}"` : ''} type="image/webp" srcset="${srcsetWebp}">`);
      }
      sources.push(`<source${media ? ` media="${media}"` : ''} type="image/${type}" srcset="${srcset}">`);
    });

    const loadingAttr = !disableLazyLoading ? ' loading="lazy"' : '';
    return `<picture>
      ${sources.join('\n      ')}
      <img${loadingAttr} src="${preferred}" alt="${imageAlt || ''}" data-src="${fallback}" data-failsafe="${imageUrl}" onerror="modulrImageSrcError(this)" style="${imgStyle}">
    </picture>`;
  }

  return `<picture><img src="${fallback}" alt="${imageAlt || ''}" style="${imgStyle}"></picture>`;
}

